import { useTranslation } from "react-i18next"
import { type UseMutationOptions, type UseMutationResult, useMutation } from "react-query"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { PredictionReview } from "../../../types/generated/api/PredictionReview"
import type { PredictionReviewRequest } from "../../../types/generated/api/PredictionReviewRequest"
import type { StandardErrorType } from "../../../utils/ErrorHandling"

export type CreatePredictionReviewRequest = PredictionReviewRequest & {
  prediction_uuid: string
}

/**
 * Custom hook for creating prediction review mutations.
 * Handles the creation of prediction reviews (marking as reviewed/revoked) with
 * success notifications and automatic query invalidation.
 *
 * @param options - Optional mutation options to override default behavior
 * @returns Mutation result object with loading state, error handling, and success callbacks
 */
export function useCreatePredictionReviewMutation(
  options?: UseMutationOptions<
    AxiosResponse<PredictionReview>,
    AxiosError<StandardErrorType>,
    CreatePredictionReviewRequest
  >,
): UseMutationResult<AxiosResponse<PredictionReview>, AxiosError<StandardErrorType>, CreatePredictionReviewRequest> {
  const { t } = useTranslation("decisions", { keyPrefix: "projectDecisions" })

  return useMutation<AxiosResponse<PredictionReview>, AxiosError<StandardErrorType>, CreatePredictionReviewRequest>(
    (payload) => KonanAPI.createPredictionReview(payload),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        NotificationUtils.toast(t("notifications.reviewSent"), { severity: "success" })
        queryClient.invalidateQueries(["project-predictions"])
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
